// Type-specific PDF body content for slips. Renders the breakdown table that
// goes INSIDE a SlipPdfFrame. Shared by the accountant's All Slips list and
// the agent's My Slips page so both surfaces show the identical rich preview.

// Dispatches to the right body by slip type.
export default function PdfBody({ slip }) {
    if (slip.type === 'Commission') return <CommissionPdfBody slip={slip} />;
    if (slip.type === 'Incentive') return <IncentivePdfBody slip={slip} />;
    return <ExtraPdfBody slip={slip} />;
}

export function CommissionPdfBody({ slip }) {
    let rows = [];
    try { rows = JSON.parse(slip.slipData || '[]'); } catch { rows = []; }
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
                <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                    <th style={thLeft}>Sale #</th>
                    <th style={thLeft}>Customer</th>
                    <th style={thLeft}>Type</th>
                    <th style={thRight}>Total Comm</th>
                    <th style={thRight}>1st Pay</th>
                    <th style={thRight}>2nd Pay</th>
                    <th style={thRight}>Repay</th>
                    <th style={{ ...thRight, borderRadius: '0 6px 6px 0' }}>Payout</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((it, i) => {
                    const repay = (it.repayment || 0) + (it.repayment2 || 0) + (it.repayment3 || 0) + (it.repayment4 || 0) + (it.repayment5 || 0);
                    return (
                        <tr key={i} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                            <td style={tdL}>{it.salesId || '—'}</td>
                            <td style={tdL}>{it.customerName || '—'}</td>
                            <td style={tdL}>{it.type || '—'}</td>
                            <td style={tdR}>{rm(it.totalComm)}</td>
                            <td style={tdR}>{it.firstPayment != null ? rm(it.firstPayment) : '—'}</td>
                            <td style={tdR}>{it.secondPayment != null ? rm(it.secondPayment) : '—'}</td>
                            <td style={{ ...tdR, color: repay < 0 ? '#dc2626' : '#334155' }}>{repay !== 0 ? rm(repay) : '—'}</td>
                            <td style={{ ...tdR, fontWeight: 600 }}>{rm(it.payout || 0)}</td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                {slip.processingFee > 0 && (
                    <>
                        <tr>
                            <td colSpan="7" style={{ ...tfootLabel, background: '#fff', borderTop: '1px solid #edf2f7' }}>Gross Comm</td>
                            <td style={{ ...tfootValue, color: '#334155', background: '#fff', borderTop: '1px solid #edf2f7' }}>{rm(slip.grossCommission)}</td>
                        </tr>
                        <tr>
                            <td colSpan="7" style={{ ...tfootLabel, background: '#fff', borderTop: '1px solid #edf2f7' }}>Processing Fee</td>
                            <td style={{ ...tfootValue, color: '#dc2626', background: '#fff', borderTop: '1px solid #edf2f7' }}>−{rm(slip.processingFee)}</td>
                        </tr>
                    </>
                )}
                <tr>
                    <td colSpan="7" style={tfootLabel}>Total Commission Payout</td>
                    <td style={tfootValue}>{rm(slip.totalPayout)}</td>
                </tr>
            </tfoot>
        </table>
    );
}

export function IncentivePdfBody({ slip }) {
    let payload = {};
    try { payload = JSON.parse(slip.slipData || '{}'); } catch { payload = {}; }
    const addonRows = Array.isArray(payload.rows) ? payload.rows : [];
    const isAddon = payload.metricType === 'Addon' && addonRows.length > 0;
    const isCases = payload.metricType === 'Cases' && addonRows.length > 0;
    return (
        <>
            {/* Achievement summary card */}
            <div style={{ background: '#eff6ff', border: '1px solid #c7e3ff', borderRadius: '8px', padding: '14px 18px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                    <div style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Campaign</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#0a7cf8' }}>{slip.campaignName || '—'}</div>
                </div>
                <div>
                    <div style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Tier Achieved</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{slip.tierAchieved || payload.tierName || '—'}</div>
                </div>
                <div>
                    <div style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Reward</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{slip.reward || payload.reward || '—'}</div>
                </div>
                <div>
                    <div style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Type</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{slip.rewardType || payload.rewardType || 'Cash'}</div>
                </div>
            </div>

            {isAddon ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                        <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                            <th style={{ ...thLeft, borderRadius: '6px 0 0 6px' }}>Sales #</th>
                            <th style={thLeft}>Customer Name</th>
                            <th style={thLeft}>Campaign Name</th>
                            <th style={thRight}>Total Incentive</th>
                            <th style={{ ...thRight, borderRadius: '0 6px 6px 0' }}>Payout</th>
                        </tr>
                    </thead>
                    <tbody>
                        {addonRows.map((it, i) => (
                            <tr key={i} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                                <td style={tdL}>{it.salesId || '—'}</td>
                                <td style={tdL}>{it.customerName || '—'}</td>
                                <td style={tdL}>{it.campaignName || slip.campaignName || '—'}</td>
                                <td style={tdR}>{rm(it.totalIncentive ?? it.amount ?? 0)}</td>
                                <td style={tdR}>{rm(it.amount ?? 0)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="3" style={tfootLabel}>Total ({addonRows.length} sales)</td>
                            <td style={{ ...tfootLabel, textAlign: 'right' }}>
                                {rm(addonRows.reduce((s, x) => s + Number(x.totalIncentive ?? x.amount ?? 0), 0))}
                            </td>
                            <td style={tfootValue}>{rm(slip.totalPayout)}</td>
                        </tr>
                    </tfoot>
                </table>
            ) : isCases ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                        <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                            <th style={{ ...thLeft, borderRadius: '6px 0 0 6px' }}>Sales #</th>
                            <th style={thLeft}>Customer Name</th>
                            <th style={thLeft}>Campaign Name</th>
                            <th style={{ ...thRight, borderRadius: '0 6px 6px 0' }}>Cases</th>
                        </tr>
                    </thead>
                    <tbody>
                        {addonRows.map((it, i) => (
                            <tr key={i} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                                <td style={tdL}>{it.salesId || '—'}</td>
                                <td style={tdL}>{it.customerName || '—'}</td>
                                <td style={tdL}>{it.campaignName || slip.campaignName || '—'}</td>
                                <td style={tdR}>{Number(it.contribution ?? 0).toFixed(1)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="2" style={tfootLabel}>Total ({addonRows.length} sales)</td>
                            <td style={{ ...tfootLabel, textAlign: 'right' }}>
                                {addonRows.reduce((s, x) => s + Number(x.contribution || 0), 0).toFixed(1)} cases → payout
                            </td>
                            <td style={tfootValue}>{rm(slip.totalPayout)}</td>
                        </tr>
                    </tfoot>
                </table>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                        <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                            <th style={{ ...thLeft, borderRadius: '6px 0 0 6px' }}>Metric</th>
                            <th style={thRight}>Achieved</th>
                            <th style={thRight}>Tier Target</th>
                            <th style={{ ...thRight, borderRadius: '0 6px 6px 0' }}>Payout</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={tdL}>{payload.metricType || 'Revenue'}</td>
                            <td style={tdR}>{rm(payload.revenue ?? payload.totalCases ?? 0)}</td>
                            <td style={tdR}>{rm(payload.target ?? 0)}</td>
                            <td style={{ ...tdR, fontWeight: 600 }}>{rm(slip.totalPayout)}</td>
                        </tr>
                    </tbody>
                </table>
            )}
        </>
    );
}

export function ExtraPdfBody({ slip }) {
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
                <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                    <th style={{ ...thLeft, borderRadius: '6px 0 0 6px' }}>Reason</th>
                    <th style={thLeft}>Description</th>
                    <th style={{ ...thRight, borderRadius: '0 6px 6px 0' }}>Amount</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td style={{ ...tdL, verticalAlign: 'top' }}>{slip.reason || slip.displayName || '—'}</td>
                    <td style={{ ...tdL, verticalAlign: 'top', whiteSpace: 'pre-line' }}>{slip.description || '—'}</td>
                    <td style={{ ...tdR, fontWeight: 600, verticalAlign: 'top' }}>{rm(slip.amount ?? slip.totalPayout)}</td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan="2" style={tfootLabel}>Total Payout</td>
                    <td style={tfootValue}>{rm(slip.amount ?? slip.totalPayout)}</td>
                </tr>
            </tfoot>
        </table>
    );
}

const rm = (v) => `RM ${new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)}`;
const thLeft = { padding: '9px 10px', textAlign: 'left', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' };
const thRight = { padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' };
const tdL = { padding: '9px 10px', borderBottom: '1px solid #edf2f7', color: '#334155' };
const tdR = { padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: '#334155' };
const tfootLabel = { textAlign: 'right', padding: '9px 14px 9px 10px', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#eff6ff', borderTop: '2px solid #0a7cf8' };
const tfootValue = { textAlign: 'right', padding: '9px 10px', fontWeight: 700, fontSize: '13px', color: '#0a7cf8', background: '#eff6ff', borderTop: '2px solid #0a7cf8' };
